import React, { useContext, useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { general } from '../../axiosConfig';
import { Context } from '../../contexts/Store';
import NotificationModal from '../includes/modals/NotificationModal';

// Icons
import dashboard_Icon from '../../assets/image/dashboard/dashboard.svg';
import accountsIcon from '../../assets/image/icons/accounts.svg';
import customerIcon from '../../assets/image/icons/customer.svg';
import inventoryIcon from '../../assets/image/icons/inventory.svg';
import purchasesIcon from '../../assets/image/icons/purchases.svg';
import reportsIcon from '../../assets/image/icons/reports.svg';
import salesIcon from '../../assets/image/icons/sales.svg';
import settingsIcon from '../../assets/image/icons/settings.svg';
import suppliersIcon from '../../assets/image/icons/supplier.svg';
import usersIcon from '../../assets/image/icons/users.svg';
import bell_Icon from '../../assets/image/dashboard/bell_black.svg';

// toggle
import ModeToggle from '../includes/ModeToggle';

// Arrow icons
import arrowDown from '../../assets/image/dashboard/down-arrow.svg';
import arrowRight from '../../assets/image/dashboard/right-arrow.svg';

function Sidebar({ collapsed }) {
  const { state } = useContext(Context);
  const location = useLocation();
  const [hover, setHover] = useState('');
  const [notificationModal, setNotificationModal] = useState(false);
  const access_token = state.user_details.access_token;
  const [notifications, setNotifications] = useState([]);
  const [openMenus, setOpenMenus] = useState({}); // Track submenu states

  const getNotifications = () => {
    general
      .get('/notifications', {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      })
      .then((response) => {
        if (response.data.StatusCode === 6000) {
          setNotifications(response.data.data);
        }
      })
      .catch((err) => console.error(err));
  };

  useEffect(() => {
    getNotifications();
  }, []);

  const toggleSubMenu = (key) => {
    setOpenMenus((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const menuItems = [
    { label: 'Dashboard', icon: dashboard_Icon,  to: '/' },
    { label: 'Customer', icon: customerIcon,  to: '/customers' },
    { label: 'Supplier', icon: suppliersIcon, to: '/suppliers' },
    {
      label: 'Item',
      icon: inventoryIcon,
      children: [
        { label: 'Item List', to: '/items' },
        { label: 'Categories', to: '/items/categories' },
        { label: 'Units', to: '/items/units' },
      ],
    },
    {
      label: 'Sales',
      icon: salesIcon,
      children: [
        { label: 'Sales List', to: '/sales' },
        { label: 'POS', to: '/sales/pos' },
        { label: 'Returns', to: '/sales/returns' },
      ],
    },
    {
      label: 'Purchase',
      icon: purchasesIcon,
      children: [
        { label: 'Purchase Orders', to: '/purchase' },
        { label: 'Goods Received', to: '/purchase/received' },
        { label: 'Returns', to: '/purchase/returns' },
      ],
    },
    { label: 'Accounts', icon: accountsIcon, to: '/my-account' },
    { label: 'Reports', icon: reportsIcon,  to: '/reports' },
    { label: 'Users', icon: usersIcon,  to: '/users' },
    { label: 'Settings', icon: settingsIcon,  to: '/settings' },
  ];

  // Helper: check if any child route is active
  const isChildActive = (children) => {
    return children?.some((child) => location.pathname.startsWith(child.to));
  };

  return (
    <SidebarContainer collapsed={collapsed}>
      <Menu>
        {menuItems.map((item, idx) => {
          const parentActive = isChildActive(item.children);
          return (
            <div key={idx}>
              {item.children ? (
                <>
                  <SidebarParent
                    collapsed={collapsed}
                    onClick={() => toggleSubMenu(item.label)}
                    onMouseEnter={() => setHover(item.label)}
                    onMouseLeave={() => setHover('')}
                    isActive={openMenus[item.label] || parentActive}
                  >
                    <img
                      src={item.icon}
                      alt={item.label}
                    />
                    {!collapsed && <span>{item.label}</span>}
                    {!collapsed && (
                      <ArrowIcon src={openMenus[item.label] ? arrowDown : arrowRight} alt="Toggle" />
                    )}
                  </SidebarParent>
                  {openMenus[item.label] && !collapsed && (
                    <SubMenu>
                      {item.children.map((sub, subIdx) => (
                        <SidebarLink key={subIdx} to={sub.to} collapsed={collapsed}>
                          <span>{sub.label}</span>
                        </SidebarLink>
                      ))}
                    </SubMenu>
                  )}
                </>
              ) : (
                <SidebarLink
                  to={item.to}
                  collapsed={collapsed}
                  onMouseEnter={() => setHover(item.label)}
                  onMouseLeave={() => setHover('')}
                >
                  <img src={item.icon} alt={item.label} />
                  {!collapsed && <span>{item.label}</span>}
                </SidebarLink>
              )}
            </div>
          );
        })}
      </Menu>

      <BottomSection>
         <ModeToggle />
      </BottomSection>
    </SidebarContainer>
  );
}

export default Sidebar;

// Styled Components
const SidebarContainer = styled.div`
  width: ${({ collapsed }) => (collapsed ? '80px' : '224px')};
  height: calc(100vh - 70px);
  margin-top: 70px;
  padding: 20px 15px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background: #fff;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 100;
  transition: width 0.3s ease;
  overflow: hidden;
  align-items: ${({ collapsed }) => (collapsed ? 'center' : 'flex-start')};
`;

const Menu = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 100%;
`;

const SidebarLink = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: ${({ collapsed }) => (collapsed ? '0' : '12px')};
  padding: 8px 10px;
  border-radius: 6px;
  text-decoration: none;
  color: #000;
  font-family: 'DM_sans';
  justify-content: ${({ collapsed }) => (collapsed ? 'center' : 'flex-start')};

  &.active {
    border: 1px solid #DADADA;
    background: #DADADA;
    color: #000;
  }

  &:hover {
    border: 1px solid #DADADA;
    background: #DADADA;
    color: #000;
  }

  span {
    font-size: 14px;
  }
`;

const SidebarParent = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ collapsed }) => (collapsed ? '0' : '12px')};
  padding: 8px 10px;
  border-radius: 6px;
  font-family: 'DM_sans';
  cursor: pointer;
  justify-content: ${({ collapsed }) => (collapsed ? 'center' : 'flex-start')};
  color: ${({ isActive }) => (isActive ? '#000' : '#000')};
  border: ${({ isActive }) => (isActive ? '1px solid #000' : '1px solid transparent')};
  background: ${({ isActive }) => (isActive ? '#DADADA' : 'transparent')};

  &:hover {
    border: 1px solid #000;
    background: #DADADA;
    color: #000;
  }

  img {
    width: 20px;
  }
`;

const ArrowIcon = styled.img`
  margin-left: auto;
  width: 12px;
`;

const SubMenu = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: 30px;
  gap: 4px;
`;

const BottomSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const IconBtn = styled.div`
  padding: 10px;
  border-radius: 8px;
  background: #DADADA;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  img {
    width: 20px;
  }
  &:hover {
    border: 1px solid #000;
  }
`;
